import React from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import type { dailyBrief } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

interface Props {
  briefing: dailyBrief | null;
  onCancel: () => void;
}

export const BriefingDialog: React.FC<Props> = ({ briefing, onCancel }) => {
  return (
    <Stack
      direction="column"
      sx={{
        width: "min(640px, 94vw)",
        maxHeight: "min(720px, 92vh)",
        bgcolor: "#fff",
        borderRadius: "10px",
        boxSizing: "border-box" as const
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        sx={{
          pt: "24px",
          px: "28px",
          pb: "20px",
          borderBottom: "1px solid #dbe8f5",
          flexShrink: 0
        }}
      >
        <Stack direction="column" gap="8px" sx={{ minWidth: 0 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: "#c09560" }} />
          <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#071f35", lineHeight: 1.2 }}>
            Daily briefing
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#92a7bc", ml: "6px" }}>
            AI-generated · {formatNewsDate(briefing?.date, { absoluteOnly: true })}
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            border: "1px solid #dbe8f5",
            bgcolor: "#fff",
            color: "#5d7782",
            "&:hover": { bgcolor: "rgba(7,31,53,0.04)" },
            "& .MuiSvgIcon-root": { fontSize: 16 }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Stack
        direction="column"
        sx={{
          px: "28px",
          py: "20px",
          flex: 1,
          minHeight: 0,
          overflowY: "auto"
        }}
      >
        {briefing?.highlights.length && (
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: "0 0 16px" }}>
            {briefing?.highlights.map((highlight, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "#5d7782",
                  py: "6px",
                  pl: "16px",
                  position: "relative",
                  borderBottom: "1px solid #dbe8f5",
                  "&:last-of-type": { borderBottom: "none" },
                  "&::before": {
                    // eslint-disable-next-line quotes
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "13px",
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    bgcolor: "#c09560",
                    display: "block"
                  }
                }}
              >
                {highlight}
              </Box>
            ))}
          </Box>
        )}

        {Boolean(briefing?.summary) && (
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.7,
              color: "#5d7782",
              m: "0 0 16px",
              whiteSpace: "pre-wrap"
            }}
          >
            {briefing?.summary}
          </Typography>
        )}
      </Stack>
      <Stack>
        <Typography
          sx={{
            fontSize: 11,
            px: "28px",
            pb: "20px",
            color: "#92a7bc",
            pt: "12px",
            borderTop: "1px solid #dbe8f5"
          }}
        >
          This summary is generated from news sources and does not constitute investment advice.
        </Typography>
      </Stack>
    </Stack>
  );
};
